import type { SimpleChanges, TemplateRef } from '@angular/core';
import { ChangeDetectorRef, Component, Input } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';

import { UserSelectors } from '@recruitee/account-data';
import { I18nService } from '@recruitee/i18n';
import type { NoteId } from '@recruitee/notes-types';
import { ReactionActions, ReactionsDataModule } from '@recruitee/reactions-data';
import type { Reactions } from '@recruitee/reactions-types';
import type { EmojiItem } from '@recruitee/symbol-data';
import { SYMBOL_EMOJI_MAP } from '@recruitee/symbol-data';

import type { ReactionData } from '../../types/reaction-data.types';
import { ReactionsComponent } from '../../ui/reactions/reactions.component';

@Component({
  selector: 'rt-note-reactions-container',
  template: ` <rt-reactions
    [reactionsData]="reactionsData"
    [projectedContentTpl]="projectedContentTpl"
    (addExistingReaction)="addExistingReaction($event)"
    (addNewReaction)="addNewReaction($event)"
    (removeReaction)="removeReaction($event)"
  />`,
  imports: [ReactionsComponent, ReactionsDataModule],
})
export class NoteReactionsContainerComponent {
  @Input() public reactions: Reactions;
  @Input() public currentAdminId: number;
  @Input() public noteId: NoteId;
  @Input() public projectedContentTpl: TemplateRef<any>;

  public reactionsData: ReactionData[] = [];

  private reactions$ = new BehaviorSubject<Reactions>([]);
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store,
    private i18n: I18nService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reactions?.currentValue) {
      this.reactions$.next(this.reactions);
    }
  }

  public ngOnInit(): void {
    this.reactions$
      .pipe(
        withLatestFrom(this.store.select(UserSelectors.idNameMap)),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(([reactions, userNames]) => {
        this.reactionsData = this.flattenReactions(reactions, userNames);
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public addNewReaction(unicode: string): void {
    this.reactionsData = [
      ...this.reactionsData,
      {
        currentUserReacted: true,
        count: 1,
        reaction: unicode,
        otherUsersWhoReacted: [],
        reactionShortName: this.getEmojiShortName(unicode),
      },
    ];
    this.cdRef.detectChanges();
    this.createReaction(unicode);
  }

  public addExistingReaction(unicode: string): void {
    this.reactionsData = this.reactionsData.reduce(
      (acc, curr) => {
        if (curr.reaction !== unicode) {
          return [...acc, curr];
        } else {
          return [
            ...acc,
            {
              ...curr,
              count: curr.count + 1,
              currentUserReacted: true,
            },
          ];
        }
      },
      <ReactionData[]>[],
    );
    this.cdRef.detectChanges();
    this.createReaction(unicode);
  }

  public removeReaction(unicode: string): void {
    this.reactionsData = this.reactionsData.reduce(
      (acc, curr) => {
        if (curr.reaction !== unicode) {
          return [...acc, curr];
        } else {
          if (curr.count === 1) {
            return acc;
          } else {
            return [
              ...acc,
              {
                ...curr,
                count: curr.count - 1,
                currentUserReacted: false,
              },
            ];
          }
        }
      },
      <ReactionData[]>[],
    );
    this.cdRef.detectChanges();
    this.deleteReaction(unicode);
  }

  private createReaction(unicode: string): void {
    this.store.dispatch(
      ReactionActions.addNoteReaction({
        payload: { data: { noteId: <number>this.noteId, unicode } },
      }),
    );
  }

  private deleteReaction(unicode: string): void {
    this.store.dispatch(
      ReactionActions.removeNoteReaction({
        payload: { data: { noteId: <number>this.noteId, unicode } },
      }),
    );
  }

  private flattenReactions(
    reactions: Reactions,
    userNames: Record<number, string | undefined>,
  ): ReactionData[] {
    return reactions.map(reaction => {
      const adminIds = reaction.reactions.reduce(
        (acc, curr) => [...acc, ...curr.adminIds],
        <number[]>[],
      );
      const currentUserReacted = adminIds.includes(this.currentAdminId);
      const otherUsersWhoReacted = adminIds
        .filter(id => id !== this.currentAdminId)
        .map(id => userNames[id] || this.i18n.translate('app.common.deleted_user'));
      const reactionShortName = this.getEmojiShortName(reaction.base);
      return {
        count: reaction.count,
        reaction: reaction.base,
        currentUserReacted,
        otherUsersWhoReacted,
        reactionShortName,
      };
    });
  }

  private getEmojiShortName(symbol: string): string {
    return (<EmojiItem>SYMBOL_EMOJI_MAP[symbol])?.shortName || '';
  }
}
